// KpiCard — Phase 4 Plan 04-05 (DIS-14, UI-SPEC §Screen 1).
//
// Single KPI tile rendered in the Dashboard header. Tap to drill into
// filtered trips list (e.g., "Active (12)" → /dispatcher/trips?status=active).

import { TOUCH_TARGET_MIN } from "../lib/tokens";

export interface KpiCardProps {
  /** Numeric value or pre-formatted string ("—" for missing data). */
  value: number | string;
  /** Display label below the value ("Active", "Problems", etc.). */
  label: string;
  /** Optional click handler — opens filtered list. */
  onClick?: () => void;
  /** Optional accessibility hint (aria-label override). */
  ariaLabel?: string;
  /** Optional test id override. */
  "data-testid"?: string;
}

export function KpiCard({
  value,
  label,
  onClick,
  ariaLabel,
  ...rest
}: KpiCardProps): React.JSX.Element {
  const testId = rest["data-testid"] ?? `kpi-card-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const aria = ariaLabel ?? `${value} ${label}. Tap to filter trips list.`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={aria}
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        minHeight: TOUCH_TARGET_MIN,
        padding: "12px 16px",
        // Theme-aware surface: secondary bg adapts light/dark via themeParams.
        // No hex fallback — Telegram injects --tg-theme-* in the Mini App WebView
        // (UIR-05, no hardcode-hex per UI-SPEC §5).
        background: "var(--tg-theme-secondary-bg-color)",
        border: "1px solid var(--tg-theme-hint-color)",
        borderRadius: 12,
        cursor: onClick ? "pointer" : "default",
        textAlign: "left",
        flex: 1,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 28,
          fontWeight: 600,
          lineHeight: 1.2,
          color: "var(--tg-theme-text-color)",
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--tg-theme-hint-color)",
        }}
      >
        {label}
      </span>
    </button>
  );
}
